import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Radio } from 'antd';

import DialogLoadState from '../common/DialogLoadState';
import { Skeleton } from '../loading';
import { getDashboardLists, DASHBOARD_SCOPE } from '../../services/dashboards';
import { normalizeId } from '../../utils/ids';
import { COLLECTION_ICON } from '../icons';

/** The picker's two modes: the person's own Overview, or a manager's choice for everyone. */
export const PICK_FOR = Object.freeze({ ME: 'me', EVERYONE: 'everyone' });

/** The value of "no dashboard of the collection's own" in the everyone picker. */
export const NO_DASHBOARD = '';

/**
 * Choosing the dashboard an Overview shows (charts A7; plan §4.6, §6.6; ruled
 * q04), in a window:
 *
 *   PICK_FOR.ME        "Show on my Overview" — every dashboard the person can
 *                      open here: pinned for everyone, their own, and the ones
 *                      pinned for everyone above this collection
 *   PICK_FOR.EVERYONE  "Show on Overview for everyone" (a manager's) — only
 *                      dashboards pinned for everyone, of this collection or
 *                      one above it, so everyone who opens Overview can open
 *                      it; and "No dashboard" when the collection has one of
 *                      its own (its Overview then falls through to the one
 *                      above, if any)
 *
 * The list is the Reports list (GET …/dashboards), grouped as Reports groups
 * it; `current` is selected at first. `onChoose(id | NO_DASHBOARD)` saves;
 * the window closes when it resolves true.
 */
export default function OverviewPickerModal({ open, mode = PICK_FOR.ME, collectionId, current = null, hasOwnChoice = false, onChoose, onClose }) {
	const [lists, setLists] = useState(null);
	const [failed, setFailed] = useState(false);
	const [attempt, setAttempt] = useState(0);
	const [value, setValue] = useState(normalizeId(current) || null);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!open || !collectionId) return undefined;
		let active = true;
		setLists(null);
		setFailed(false);
		setValue(normalizeId(current) || null);
		getDashboardLists(collectionId)
			.then((data) => { if (active) setLists(data); })
			.catch(() => { if (active) setFailed(true); });
		return () => { active = false; };
	}, [open, collectionId, current, attempt]);

	const groups = useMemo(() => {
		if (!lists) return [];
		const everyone = lists.dashboards.filter((d) => d?.scope === DASHBOARD_SCOPE.COLLECTION);
		const yours = lists.dashboards.filter((d) => d?.scope !== DASHBOARD_SCOPE.COLLECTION);
		// This collection's own only: a sub-collection shows no dashboard of
		// the collection above it (owner ruling 2026-10-21, KPR-1084).
		const out = [];
		if (everyone.length) out.push({ key: 'everyone', label: 'Pinned for everyone', glyph: true, rows: everyone });
		if (mode === PICK_FOR.ME && yours.length) out.push({ key: 'yours', label: 'Yours', rows: yours });
		return out;
	}, [lists, mode]);

	const everyoneMode = mode === PICK_FOR.EVERYONE;
	const title = everyoneMode ? 'Show on Overview for everyone' : 'Show on my Overview';
	const offerNone = everyoneMode && hasOwnChoice;
	const empty = lists && !groups.length;
	const unchanged = (value || null) === (normalizeId(current) || null) && !(offerNone && value === NO_DASHBOARD && current);

	const save = async () => {
		if (value === null || typeof onChoose !== 'function') return;
		setSaving(true);
		try {
			const ok = await onChoose(value);
			if (ok !== false) onClose?.();
		} finally {
			setSaving(false);
		}
	};

	return (
		<Modal
			open={open}
			title={title}
			onCancel={onClose}
			onOk={save}
			okText='Show it'
			okButtonProps={{ disabled: value === null || unchanged || Boolean(empty), loading: saving }}
			destroyOnHidden
			className='overview-picker'
		>
			<p className='overview-picker__intro'>
				{everyoneMode
					? 'Everyone who opens this collection sees this dashboard on its Overview, unless they chose their own.'
					: 'Only you see this choice. Everyone else keeps the collection\'s Overview.'}
			</p>
			<DialogLoadState
				loaded={Boolean(lists)}
				error={failed}
				isEmpty={Boolean(lists) && !groups.length && !offerNone}
				empty={(
					<p className='overview-picker__quiet'>
						{everyoneMode
							? 'No dashboard is pinned for everyone yet. Pin one for everyone from Reports first.'
							: 'No dashboards yet. Make one from Reports first.'}
					</p>
				)}
				label='the dashboards'
				onRetry={() => setAttempt((n) => n + 1)}
				skeleton={<Skeleton variant='list-row' count={3} />}
				minHeight={140}
			>
				<Radio.Group className='overview-picker__list' value={value} onChange={(e) => setValue(e.target.value)} aria-label={title}>
					{offerNone ? (
						<Radio className='overview-picker__row' value={NO_DASHBOARD}>No dashboard</Radio>
					) : null}
					{groups.map((group) => (
						<div key={group.key} className='overview-picker__group' role='group' aria-label={group.label}>
							<div className='micro-label overview-picker__label'>
								{group.glyph ? <COLLECTION_ICON aria-hidden='true' className='overview-picker__glyph' /> : null}
								{group.label}
							</div>
							{group.rows.map((d) => (
								<Radio key={normalizeId(d._id)} className='overview-picker__row' value={normalizeId(d._id)}>
									{d.name || 'Untitled dashboard'}
								</Radio>
							))}
						</div>
					))}
				</Radio.Group>
			</DialogLoadState>
		</Modal>
	);
}
